import { createFileRoute, Link, Navigate } from '@tanstack/react-router'
import { css } from '../../../../../styled-system/css'
import { contentWrapper } from '../../../../styles/contentWrapper'
import { titleWrapper, pageTitle, sectionWrapper } from '../../../../styles/sectionStyles'
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import Add from '~icons/material-symbols/add.svg'
import CheckCircle from '~icons/material-symbols/check-circle.svg'
import Close from '~icons/material-symbols/close.svg'
import { input, inputLabel, inputWrapper } from '../../../../styles/inputStyles'
import { button } from '../../../../styles/buttonStyles'
import { generateReadableId } from '../../../../libs/utils'
import { useEffect, useState } from 'react'
import { useCreateStore, useGetAllCities, useGetCategories, useGetMyCompanies, useUpdateStoreImage } from '../../../../libs/admin/hooks'
import type { z } from 'zod'
import { ValidatableTextInput, ValidatableLatLngInput } from '../../../../components/validatable-inputs/text-input'
import { ValidatableSelect } from '../../../../components/validatable-inputs/select'
import { atom, useAtom, useAtomValue } from 'jotai'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import toast from 'react-hot-toast'
import { dialogBackdrop } from '../../../../styles/dialogStyles'
import { LoadingOverlay } from '../../../../components/loading-overlay'
import { StoreImageInput } from '../../../../components/store-image-input'
import { Image } from 'schema/gen/es/chiikipoint/model/v2/model_pb'
import { storeInfoSchema } from '../-schemas/store-info'
import { ValidatableDayPicker } from '../../../../components/validatable-inputs/day-picker'
import { set } from 'date-fns'
import { Timestamp } from '@bufbuild/protobuf'

export const Route = createFileRoute('/admin/companies/$companyId/newStores')({
  component: RouteComponent,
})

const useCompanyData = () => {
  const { companyId } = Route.useParams()
  const { data: myCompanies, isLoading: isCompaniesLoading, isFetching: isCompaniesFetching } = useGetMyCompanies()
  const { data: categories, isLoading: isCategoryLoading } = useGetCategories()
  const { data: cities, isLoading: isCityLoading } = useGetAllCities()
  const isLoading = isCompaniesLoading || isCategoryLoading || isCityLoading
  if (isLoading) {
    return { isLoading, allStoreSlugs: [] as string[] }
  }
  if (!myCompanies) {
    throw new Error('My companies data not found')
  }
  const allStoreSlugs = myCompanies.flatMap((company) =>
    (company.stores ?? [])
      .map((store) => store.slug)
      .filter((slug): slug is string => Boolean(slug))
  )
  const companyWithStores = myCompanies.find((c) => c.company?.id === companyId)
  if (!companyWithStores) {
    if (isCompaniesFetching) {
      // 新規作成時には事業者情報がない場合があるため、isFetchingでもローディング状態を返す
      return { isLoading: true, allStoreSlugs: [] as string[] }
    }
    throw new Error('Company not found')
  }
  return {
    companyId,
    companyWithStores,
    categories,
    cities,
    allStoreSlugs,
    isLoading,
  }
}

type storeInfoType = z.infer<typeof storeInfoSchema>

function RouteComponent() {
  return <Layout />
}

type DirtyStoreInfo = storeInfoType & {
  key: string; // 複数店舗を新規登録するときの状態管理に使用
  image?: File; // 画像ファイルを保持するためのフィールド
}

const StoreInfoSection = ({
  companyName,
  storeInfo,
  setStoreInfo,
  deletable = false,
  onDelete,
}: {
  companyName: string;
  storeInfo: DirtyStoreInfo;
  setStoreInfo: (storeInfo: DirtyStoreInfo) => void;
  deletable?: boolean;
  onDelete?: () => void;
}) => {
  // validate store info
  const parseResult = storeInfoSchema.safeParse(storeInfo)
  const fieldErrors = parseResult.error?.format() 

  // Additional Validation for duplicate slug
  const { allStoreSlugs, categories, cities, isLoading } = useCompanyData()
  const storeInfos = useAtomValue(storeInfosAtom)
  const qrCodeIdentifiers = storeInfos
    .map(storeInfo => storeInfo.slug)
    .filter((slug): slug is string => Boolean(slug))
  const slugSet = new Set<string>()
  const hasDuplicateSlug = [...allStoreSlugs, ...qrCodeIdentifiers].some((slug) => {
    if (!slug) return false
    if (slugSet.has(slug)) return true
    slugSet.add(slug)
    return false
  })

  if (isLoading) {
    return null
  }
  if (!categories) {
    throw new Error('Category data not found')
  }
  if (!cities) {
    throw new Error('City data not found')
  }

  return (
    <div className={sectionWrapper()}>
      {/* 削除ボタン */}
      {deletable && (
        <div className={css({
          position: 'absolute',
          top: '0',
          right: '0',
          padding: '8px',
        })}>
          <button
            type='button'
            className={css({
              ...button.raw({ color: 'ghost' }),
              color: 'red.500',
              _hover: {
                backgroundColor: 'red.50',
              },
            })}
            onClick={onDelete}
          >
            <Close />
            削除
          </button>
        </div>
      )}
      <div className={inputWrapper()}>
        <div className={inputLabel()}>
          事業者名
        </div>
        <input type='text' disabled className={input()} value={companyName} />
      </div>
      <ValidatableTextInput
        title='店舗名'
        placeholder='ポケットストア'
        value={storeInfo.name ?? ''}
        onChange={(value) => {
          setStoreInfo({
            ...storeInfo,
            name: value,
          })
        }}
        isValid={!fieldErrors?.name}
        validationMessage={fieldErrors?.name?._errors[0] ?? 'OK'}
        type='string'
        isRequired
      />
      <ValidatableSelect
        title='市区町村'
        value={storeInfo.cityAddress ?? ''}
        onChange={(value) => {
          setStoreInfo({
            ...storeInfo,
            cityAddress: value,
            fullAddress: value,
          })
        }}
        isValid={!fieldErrors?.cityAddress}
        validationMessage={fieldErrors?.cityAddress?._errors[0] ?? 'OK'}
        options={['', ...cities].map((e) => ({ label: e, value: e }))}
        isRequired
      />
      <ValidatableTextInput
        title='住所'
        placeholder='○○県○○市○○町○丁目○番地○'
        value={storeInfo.fullAddress ?? ''}
        onChange={(value) => {
          setStoreInfo({
            ...storeInfo,
            fullAddress: value,
          })
        }}
        isValid={!fieldErrors?.fullAddress}
        validationMessage={fieldErrors?.fullAddress?._errors[0] ?? 'OK'}
        type='string'
      />
      <ValidatableSelect
        title='カテゴリ'
        value={storeInfo.category ?? ''}
        onChange={(value) => {
          setStoreInfo({
            ...storeInfo,
            category: value,
          })
        }}
        isValid={!fieldErrors?.category}
        validationMessage={fieldErrors?.category?._errors[0] ?? 'OK'}
        options={['', ...categories].map((e) => ({ label: e, value: e }))}
        isRequired
      />
      <ValidatableLatLngInput
        title="店舗の位置"
        value={storeInfo.coordinate}
        onChange={(value) => {
          setStoreInfo({
            ...storeInfo,
            coordinate: value,
          })
        }}
        isValid={!fieldErrors?.coordinate}
        validationMessage={fieldErrors?.coordinate?._errors[0] ?? 'OK'}
      />
      <ValidatableTextInput
        title='店舗QRコード識別子'
        description='任意の識別子を指定できます。共通QRコードとして利用する場合は、別テナントの店舗と同じ識別子を指定します。'
        placeholder='英数字8文字'
        value={storeInfo.slug ?? ''}
        onChange={(value) => {
          setStoreInfo({
            ...storeInfo,
            slug: value,
          })
        }}
        isValid={!fieldErrors?.slug && !hasDuplicateSlug}
        validationMessage={fieldErrors?.slug?._errors[0] ?? (hasDuplicateSlug ? 'このQRコード識別子はすでに使用されています' : 'OK')}
        type='string'
        isRequired
      />
      <StoreImageInput
        onFileChange={(file) => {
          setStoreInfo({
            ...storeInfo,
            image: file ?? undefined,
          })
        }}
        shouldShowResetButton
      />
      <div className={css({
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <ValidatableDayPicker
          title='利用開始日'
          description='店舗の利用開始日を指定します。'
          placeholder='yyyy/MM/dd'
          value={storeInfo.operationalPeriod?.openedAt ? new Date(storeInfo.operationalPeriod.openedAt) : undefined}
          onChange={(date) => {
            setStoreInfo({
              ...storeInfo,
              operationalPeriod: {
                ...storeInfo.operationalPeriod,
                openedAt: date ? set(date, { hours: 0, minutes: 0, seconds: 0 }) : set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
              }
            })
          }}
          isValid={!fieldErrors?.operationalPeriod}
          validationMessage={fieldErrors?.operationalPeriod?._errors[0] ?? fieldErrors?.operationalPeriod?.openedAt?._errors[0] ?? 'OK'}
          isRequired
          format='yyyy/MM/dd'
        />
        <ValidatableDayPicker
          title='利用終了日'
          description='店舗の利用終了日を指定します。指定した日時の23:59:59まで利用可能です。'
          placeholder='yyyy/MM/dd'
          value={storeInfo.operationalPeriod?.closedAt ? new Date(storeInfo.operationalPeriod.closedAt) : undefined}
          onChange={(date) => {
            setStoreInfo({
              ...storeInfo,
              operationalPeriod: {
                ...storeInfo.operationalPeriod,
                closedAt: date ? set(date, { hours: 23, minutes: 59, seconds: 59 }) : undefined,
              }
            })
          }}
          isValid={!fieldErrors?.operationalPeriod}
          validationMessage={fieldErrors?.operationalPeriod?._errors[0] ?? fieldErrors?.operationalPeriod?.closedAt?._errors[0] ?? 'OK'}
          format='yyyy/MM/dd'
        />
      </div>
    </div>
  )
}

const generateNewStoreInfo = () => {
  return {
    key: generateReadableId(32),
    slug: generateReadableId(8),
    name: '',
    category: '',
    coordinate: undefined,
    fullAddress: '',
    cityAddress: '',
    operationalPeriod: {
      openedAt: set(new Date(), { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }),
      closedAt: undefined,
    },
  }
}

const storeInfosAtom = atom<DirtyStoreInfo[]>([])

const Layout = () => {
  const { companyWithStores, isLoading, allStoreSlugs } = useCompanyData()
  const [storeInfos, setStoreInfos] = useAtom(storeInfosAtom)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { companyId } = Route.useParams()
  const { mutateAsync } = useCreateStore()
  const { mutateAsync: mutateAsyncImage } = useUpdateStoreImage()

  // reset storeInfos on mount
  useEffect(() => {
    setStoreInfos([generateNewStoreInfo()])
  }, [setStoreInfos])

  const handleCreateStore = async (storeInfo: DirtyStoreInfo) => {
    const validatedStoreInfo = storeInfoSchema.safeParse(storeInfo)
    if (!validatedStoreInfo.success) {
      throw new Error("Invalid store information")
    }
    const data = validatedStoreInfo.data;
    const { storeId } = await mutateAsync({
      companyId,
      name: data.name,
      slug: data.slug,
      category: data.category,
      latitude: data.coordinate?.lat,
      longitude: data.coordinate?.lng,
      fullAddress: data.fullAddress,
      cityAddress: data.cityAddress,
      openedAt: data.operationalPeriod.openedAt ? Timestamp.fromDate(data.operationalPeriod.openedAt) : undefined,
      closedAt: data.operationalPeriod.closedAt ? Timestamp.fromDate(data.operationalPeriod.closedAt) : undefined,
    })
    const image = storeInfo.image
    if (image) {
      const imageObject = new Image({
        image: new Uint8Array(await image.arrayBuffer()),
        mimeType: image.type,
      })
      await mutateAsyncImage({
        storeId,
        image: imageObject,
      })
    }
  }

  const handleCreateStores = async () => {
    try {
      const promises = storeInfos.map(handleCreateStore)
      await Promise.all(promises)
      setIsDialogOpen(true)
      setStoreInfos([generateNewStoreInfo()])
    } catch (error) {
      toast.error("店舗の登録に失敗しました")
    }
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
  }

  // validation
  const validatedStoreInfos = storeInfos.map((storeInfo) => storeInfoSchema.safeParse(storeInfo))
  const qrCodeIdentifiers = storeInfos
    .map(storeInfo => storeInfo.slug)
    .filter((slug): slug is string => Boolean(slug))
  const slugSet = new Set<string>()
  const hasDuplicateSlug = [...allStoreSlugs, ...qrCodeIdentifiers].some((slug) => {
    if (!slug) return false
    if (slugSet.has(slug)) return true
    slugSet.add(slug)
    return false
  })
  const isValid = validatedStoreInfos.every(result => result.success) && !hasDuplicateSlug

  if (isLoading) {
    return (
      <div className={contentWrapper()}>
        <LoadingOverlay />
      </div>
    )
  }
  if (!companyWithStores) {
    return <Navigate to='/admin/companies' />
  }
  if (!(companyWithStores?.company)) {
    return null
  }
  const company = companyWithStores.company

  return (
    <div className={contentWrapper()}>
      <div className={titleWrapper()}>
        <h1 className={pageTitle()}>
          <div className={css({ display: 'flex', alignItems: 'center', gap: '8px' })}>
            <Link to={".."}>
              <ArrowBack />
            </Link>
            店舗登録
          </div>
        </h1>
      </div>
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        {/* 店舗情報編集 */}
        {storeInfos.map((storeInfo, index) => (
          <StoreInfoSection
            key={storeInfo.key}
            companyName={company.name}
            storeInfo={storeInfo}
            setStoreInfo={(newStoreInfo) => {
              const newStoreInfos = [...storeInfos]
              newStoreInfos[index] = newStoreInfo
              setStoreInfos(newStoreInfos)
            }}
            deletable={storeInfos.length > 1}
            onDelete={() => {
              const newStoreInfos = [...storeInfos]
              newStoreInfos.splice(index, 1)
              setStoreInfos(newStoreInfos)
            }}
          />
        ))}
        <button
          type='button'
          className={css(
            button.raw(),
            { width: '100%' }
          )}
          onClick={() => {
            setStoreInfos([
              ...storeInfos,
              generateNewStoreInfo(),
            ])
          }}
        >
          <Add />
          さらに店舗を追加
        </button>
        <button
          type="button"
          className={button({ color: 'primary' })}
          disabled={!isValid}
          onClick={handleCreateStores}
        >
          登録
        </button>
      </div>
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        className={dialogBackdrop()}
      >
        <DialogPanel className={css({
          backgroundColor: 'white',
          width: '320px',
          margin: 'auto',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        })}>
          <CheckCircle
            className={css({
              width: '96px',
              height: '96px',
              color: 'text.accentSuccess',
              margin: '0 auto',
            })}
          />
          <DialogTitle
            className={css({
              fontSize: '16px',
              textAlign: 'center',
            })}
          >店舗を登録しました</DialogTitle>
          <div className={css({
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
          })}>
            <Link
              to='/admin/companies/$companyId'
              params={{
                companyId: companyId,
              }}
            >
              <button
                type='button'
                className={button({ color: 'ghost' })}
              >
                閉じる
              </button>
            </Link>
          </div>
        </DialogPanel>
      </Dialog >
    </div>
  )
}
